/* SAEGE - UGC strip behaviour.
 *
 * Three jobs:
 *  1. Full bleed on mobile, measured rather than guessed. The obvious CSS
 *     trick, margin-left: calc(50% - 50vw), assumes the strip's container is
 *     centred in the viewport. Here it is not - the strip lives in the buy
 *     column, which on desktop is the right-hand column, so that rule blows
 *     the page out sideways and creates horizontal page scroll. Instead the
 *     real distance to each viewport edge is measured and written to a custom
 *     property, which the stylesheet turns into a negative margin. Exact at
 *     any gutter, and it cannot overflow the page.
 *  2. Autoplay only the tiles actually on screen. Video src is withheld until
 *     a tile intersects, so landing on the PDP costs the poster images and
 *     nothing else - six autoplaying videos in the middle of the buy column
 *     would undo the weight we saved elsewhere. Honours prefers-reduced-motion
 *     by never autoplaying.
 *  3. A lightbox on tap: full size, with sound and controls.
 *
 * Markup comes from snippets/saege-ugc-strip.liquid, styles from saege-ugc.css.
 */
(function () {
  'use strict';

  var root = document.querySelector('[data-saege-ugc]');
  if (!root) return;

  var buttons = [].slice.call(root.querySelectorAll('.saege-ugc__open'));
  if (!buttons.length) return;

  var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ---- 1. measured full bleed (mobile only) ---- */
  var MAX_BLEED = 64; /* a sane ceiling, in case the layout is ever unusual */

  function bleed() {
    /* always clear first: once the negative margins are applied the element
       already touches the edges, so measuring without resetting would read
       zero and the bleed would collapse on the next call */
    root.style.removeProperty('--saege-bleed-l');
    root.style.removeProperty('--saege-bleed-r');

    if (window.innerWidth >= 768) return;

    var r = root.getBoundingClientRect();
    var l = Math.round(r.left);
    var rt = Math.round(window.innerWidth - r.right);
    if (l < 0) l = 0; else if (l > MAX_BLEED) l = MAX_BLEED;
    if (rt < 0) rt = 0; else if (rt > MAX_BLEED) rt = MAX_BLEED;

    root.style.setProperty('--saege-bleed-l', l + 'px');
    root.style.setProperty('--saege-bleed-r', rt + 'px');
  }

  bleed();
  var bt;
  function rebleed() { clearTimeout(bt); bt = setTimeout(bleed, 120); }
  window.addEventListener('resize', rebleed);
  window.addEventListener('orientationchange', rebleed);

  /* ---- 2. play what is visible ---- */
  if ('IntersectionObserver' in window && !reduce) {
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        var vid = entry.target.querySelector('video');
        if (!vid) return;
        if (entry.isIntersecting) {
          if (!vid.getAttribute('src')) {
            var s = vid.getAttribute('data-src');
            if (s) vid.setAttribute('src', s);
          }
          var p = vid.play();
          if (p && p.catch) p.catch(function () {});
          entry.target.classList.add('is-playing');
        } else {
          vid.pause();
          entry.target.classList.remove('is-playing');
        }
      });
    }, { threshold: 0.5 });
    buttons.forEach(function (b) { if (b.querySelector('video')) io.observe(b); });
  }

  /* ---- 3. lightbox ---- */

  /* Shoppable tiles are excluded from the lightbox. assets/saege-shop-the-look.js
     already binds anything carrying data-saege-stl-open, and if both handlers
     fired the shopper would get two overlays stacked on each other.
     They deliberately stay in `buttons`, which section 2 uses: filtering that
     array instead would stop every shoppable tile autoplaying. Keeping one
     array would be wrong the other way - lightbox prev/next would step onto a
     shoppable asset and show it with none of its products. */
  var lbButtons = buttons.filter(function (b) {
    return !b.hasAttribute('data-saege-stl-open');
  });

  var items = lbButtons.map(function (b) {
    return { type: b.getAttribute('data-type'), src: b.getAttribute('data-src'), poster: b.getAttribute('data-poster') || '' };
  });

  var lb, stage, prevBtn, nextBtn, index = 0, lastFocus = null;

  function build() {
    lb = document.createElement('div');
    lb.className = 'saege-ugc-lb';
    lb.setAttribute('role', 'dialog');
    lb.setAttribute('aria-modal', 'true');
    lb.setAttribute('aria-label', 'Customer content');
    lb.hidden = true;
    lb.innerHTML =
      '<div class="saege-ugc-lb__stage"></div>' +
      '<button type="button" class="saege-ugc-lb__btn saege-ugc-lb__close" aria-label="Close">&times;</button>' +
      '<button type="button" class="saege-ugc-lb__btn saege-ugc-lb__prev" aria-label="Previous">&#8249;</button>' +
      '<button type="button" class="saege-ugc-lb__btn saege-ugc-lb__next" aria-label="Next">&#8250;</button>';
    document.body.appendChild(lb);
    stage = lb.querySelector('.saege-ugc-lb__stage');
    prevBtn = lb.querySelector('.saege-ugc-lb__prev');
    nextBtn = lb.querySelector('.saege-ugc-lb__next');

    lb.querySelector('.saege-ugc-lb__close').addEventListener('click', close);
    prevBtn.addEventListener('click', function () { show(index - 1); });
    nextBtn.addEventListener('click', function () { show(index + 1); });
    lb.addEventListener('click', function (e) { if (e.target === lb) close(); });
    document.addEventListener('keydown', function (e) {
      if (!lb || lb.hidden) return;
      if (e.key === 'Escape') close();
      else if (e.key === 'ArrowLeft') show(index - 1);
      else if (e.key === 'ArrowRight') show(index + 1);
    });
  }

  function show(i) {
    if (i < 0 || i >= items.length) return;
    index = i;
    var it = items[i];
    stage.innerHTML = '';
    if (it.type === 'video') {
      var v = document.createElement('video');
      v.src = it.src;
      if (it.poster) v.poster = it.poster;
      v.controls = true;
      v.autoplay = true;
      v.loop = true;
      v.playsInline = true;
      v.setAttribute('playsinline', '');
      stage.appendChild(v);
      var p = v.play();
      if (p && p.catch) p.catch(function () {});
    } else {
      var img = document.createElement('img');
      img.src = it.src;
      img.alt = '';
      stage.appendChild(img);
    }
    prevBtn.disabled = i === 0;
    nextBtn.disabled = i === items.length - 1;
  }

  function open(i) {
    if (!lb) build();
    lastFocus = document.activeElement;
    lb.hidden = false;
    document.documentElement.classList.add('saege-ugc-lock');
    document.body.classList.add('saege-ugc-lock');
    show(i);
    lb.querySelector('.saege-ugc-lb__close').focus();
  }

  function close() {
    if (!lb) return;
    stage.innerHTML = '';
    lb.hidden = true;
    document.documentElement.classList.remove('saege-ugc-lock');
    document.body.classList.remove('saege-ugc-lock');
    if (lastFocus && lastFocus.focus) lastFocus.focus();
  }

  lbButtons.forEach(function (b, i) {
    b.addEventListener('click', function (e) { e.preventDefault(); open(i); });
  });
})();
